// src/api/apiClient.ts
import axios from 'axios';
import { getRememberMeStatus } from '../utils/storage'; // Utility function for "remember me"
import { refreshAccessToken } from './auth/api';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

// Process queued requests after token refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const handleUnauthorizedError = () => {
  if (getRememberMeStatus()) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionKey');
  } else {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('sessionKey');
  }

  // Only redirect if not already on /login
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Create an Axios instance with default configurations
const apiClient = axios.create({
  baseURL: process.env.NX_PUBLIC_API_URL || 'https://api.example.com', // Base URL for requests
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor (Optional: Add Authorization Token)
apiClient.interceptors.request.use(
  (config) => {
    const token = getRememberMeStatus()
      ? localStorage.getItem('authToken') // Use localStorage if "remember me" is enabled
      : sessionStorage.getItem('authToken'); // Use sessionStorage otherwise

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (
      config.url &&
      (config.url.includes('/driver/validVideo') ||
        config.url.includes('/driver/uploadDocuments') ||
        config.url.includes('/support/createTicket') ||
        config.url.includes('/support/editTicket') ||
        config.url.includes('device/device-bulk-upload') ||
        config.url.includes('/vehicle/vehicle-bulk-upload') ||
        config.url.includes('/geofence/geofence-bulk-upload') ||
        config.url.includes('/address/address-bulk-upload') ||
        config.url.includes('/route/route-bulk-upload') ||
        config.url.includes('/trips/trip-bulk-upload') ||
        config.url.includes('/device-ota/save-apk') ||
        config.url?.includes('/api/documents/add') ||
        config.url?.includes('/api/documents/edit'))
    ) {
      // Remove content-type and allow Axios/browser to set multipart/form-data automatically
      delete config.headers['Content-Type'];
      config.headers['Content-Type'] = 'multipart/form-data';
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response Interceptor (Handle Errors and Token Refresh)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    console.info('API Error:', error, error.status);

    if (error.response) {
      console.error('API Error:', error.response);

      // Handle unauthorized access (401) - attempt token refresh
      if (error.response.status === 401 && !originalRequest._retry) {
        const refreshToken = getRememberMeStatus()
          ? localStorage.getItem('refreshToken')
          : sessionStorage.getItem('refreshToken');
        const sessionKey = getRememberMeStatus()
          ? localStorage.getItem('sessionKey')
          : sessionStorage.getItem('sessionKey');

        if (refreshToken) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return apiClient(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const response = await refreshAccessToken(refreshToken, sessionKey || undefined);

            const storage = getRememberMeStatus() ? localStorage : sessionStorage;
            storage.setItem('authToken', response.data);

            processQueue(null, response.data);

            originalRequest.headers.Authorization = `Bearer ${response.data}`;
            return apiClient(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            handleUnauthorizedError();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          handleUnauthorizedError();
        }
      } else if (error.response.status === 403) {
        // For 403 errors, just logout
        return Promise.reject(error);
      }
    } else if (error.message === 'Network Error' && !error.response) {
      handleUnauthorizedError();
    }

    return Promise.reject(error);
  },
);

export default apiClient;
